// src/hooks/useScoring.js
import { useState, useCallback } from 'react';
import {
  getInfoAvailableScoring,
  getInfoStoredScoring,
  validateTitularScoring,
  validateWarrantorScoring,
  saveScoring,
  storeIncompleteScoring,
  parseValidationResult,
  storeWarrantorInformation,
} from '../services/scoringService';
import { extractIdFromText } from '../constants/scoringConstants';
import Swal from 'sweetalert2';
/**
 * Hook personalizado para manejar operaciones de scoring
 */
export const useScoring = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  /**
   * Cargar información de scoring según el tipo de caso
   * - DISPONIBLE: Solo GetInfoAvailableScoring (datos básicos del consorcio)
   * - STORED: Solo GetInfoStoredScoring (datos guardados previamente - casos incompletos/rechazados)
   * @param {string} group - Número de grupo
   * @param {string} order - Número de orden
   * @param {string} caseType - Tipo de caso: 'DISPONIBLE' o 'STORED'
   */
  const loadScoringInfo = useCallback(async (group, order, caseType = 'DISPONIBLE') => {
    setLoading(true);
    setError(null);
    try {
      let availableData = null;
      let storedData = null;
      // Llamar al endpoint apropiado según el tipo de caso
      if (caseType === 'DISPONIBLE') {
        // Casos disponibles → solo GetInfoAvailable
        try {
          availableData = await getInfoAvailableScoring(group, order);
        } catch (availableErr) {
          throw new Error('No se pudieron obtener datos del caso disponible');
        }
      } else {
        // Casos incompletos/rechazados (STORED) → solo GetInfoStored
        try {
          storedData = await getInfoStoredScoring(group, order, 0);
        } catch (storedErr) {
          throw new Error('No se pudieron obtener datos del caso guardado');
        }
      }
      // 3️⃣ Usar los datos del endpoint llamado
      if (!availableData && !storedData) {
        throw new Error('No se pudieron obtener datos');
      }
      // Determinar qué fuente de datos usar
      const sourceData = availableData || storedData;
      const combinedData = {
        group: sourceData?.group || group,
        order: sourceData?.order || order,
        errMsg: null,
        titularData: sourceData?.titularData || {},
        planData: sourceData?.planData || {},
        scoringDocuments: sourceData?.scoring_documents || [],
        warrantors: Array.isArray(sourceData?.warrantors) ? sourceData.warrantors : [],
        apodData: sourceData?.apodData || null,
      };
      setLoading(false);
      return combinedData;
    } catch (err) {
      console.error('[useScoring] ❌ Error al cargar scoring:', err);
      setError(err.message);
      setLoading(false);
      Swal.fire({
        icon: 'error',
        title: 'Error al cargar scoring',
        text: err.message || 'No se pudo cargar la información del scoring',
        confirmButtonColor: '#ef4444',
      });
      throw err;
    }
  }, []);
  /**
   * Validar titular y retornar información sobre garantes requeridos
   */
  const validateTitular = useCallback(async (dni, quotaValue, income, debtPercent) => {
    setLoading(true);
    setError(null);
    try {
      const response = await validateTitularScoring(dni, quotaValue, income, debtPercent);
      const parsedResult = parseValidationResult(response);
      setLoading(false);
      // No mostrar alertas - la UI ya muestra la información necesaria
      return {
        ...response,
        parsed: parsedResult,
      };
    } catch (err) {
      setError(err.message);
      setLoading(false);
      Swal.fire({
        icon: 'error',
        title: 'Error en la consulta. Por favor, inténtelo nuevamente.',
        text: err.message,
        confirmButtonColor: '#ef4444',
      });
      throw err;
    }
  }, []);
  /**
   * Validar garante
   */
  const validateWarrantor = useCallback(async (dni, quotaValue, income, debtPercent) => {
    setLoading(true);
    setError(null);
    try {
      const response = await validateWarrantorScoring(dni, quotaValue, income, debtPercent);
      setLoading(false);
      // No mostrar alertas - la UI ya muestra la información necesaria
      return response;
    } catch (err) {
      setError(err.message);
      setLoading(false);
      Swal.fire({
        icon: 'error',
        title: 'Error en la consulta. Por favor, inténtelo nuevamente.',
        text: err.message,
        confirmButtonColor: '#ef4444',
      });
      throw err;
    }
  }, []);
  /**
   * Guardar scoring
   */
  const saveScoringData = useCallback(async (scoringData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await saveScoring(scoringData);
      setLoading(false);
      await Swal.fire({
        icon: 'success',
        title: 'Scoring Guardado',
        text: 'El scoring ha sido guardado correctamente',
        confirmButtonColor: '#ef4444',
        timer: 2000,
      });
      return response;
    } catch (err) {
      setError(err.message);
      setLoading(false);
      Swal.fire({
        icon: 'error',
        title: 'Error al guardar scoring',
        text: err.message,
        confirmButtonColor: '#ef4444',
      });
      throw err;
    }
  }, []);
  /**
   * Guardar scoring incompleto
   */
  const saveIncompleteScoringData = useCallback(async (scoringData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await storeIncompleteScoring(scoringData);
      setLoading(false);
      await Swal.fire({
        icon: 'success',
        title: 'Caso Guardado',
        text: 'El caso ha sido guardado correctamente.',
        confirmButtonColor: '#ef4444',
        timer: 2000,
      });
      return response;
    } catch (err) {
      setError(err.message);
      setLoading(false);
      Swal.fire({
        icon: 'error',
        title: 'Error al guardar caso',
        text: err.message,
        confirmButtonColor: '#ef4444',
      });
      throw err;
    }
  }, []);
  /**
   * Guardar información del garante
   */
  const saveWarrantorData = useCallback(async (idScoring, warrantorData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await storeWarrantorInformation(idScoring, warrantorData);
      setLoading(false);
      return response;
    } catch (err) {
      setError(err.message);
      setLoading(false);
      Swal.fire({
        icon: 'error',
        title: 'Error al guardar garante',
        text: err.message,
        confirmButtonColor: '#ef4444',
      });
      throw err;
    }
  }, []);
  return {
    loading,
    error,
    loadScoringInfo,
    validateTitular,
    validateWarrantor,
    saveScoringData,
    saveIncompleteScoringData,
    saveWarrantorData,
  };
};
/**
 * Convertir fecha de yyyy-mm-dd, dd-mm-yyyy o dd/mm/yyyy a formato dd/mm/yyyy (para mostrar en inputs)
 */
const convertirFechaParaInput = (fecha) => {
  if (!fecha || fecha === 'N/A' || fecha === '') {
    return '';
  }
  try {
    // Si ya está en formato dd/mm/yyyy, devolverla tal cual
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(fecha)) {
      return fecha;
    }
    // Si está en formato dd-mm-yyyy, convertir a dd/mm/yyyy
    if (/^\d{2}-\d{2}-\d{4}$/.test(fecha)) {
      return fecha.replace(/-/g, '/');
    }
    // Si está en formato yyyy-mm-dd, convertir a dd/mm/yyyy
    if (/^\d{4}-\d{2}-\d{2}$/.test(fecha)) {
      const [anio, mes, dia] = fecha.split('-');
      return `${dia}/${mes}/${anio}`;
    }
    return fecha;
  } catch (error) {
    console.error('[convertirFechaParaInput] Error convirtiendo fecha:', error);
    return fecha;
  }
};
/**
 * Limpiar y formatear número de documento (remover formato CUIL/CUIT)
 * Ejemplos:
 *   "20-30123123-7" → "30123123"
 *   "30123123" → "30123123"
 *   "20301231237" → "30123123"
 */
const cleanDocumentNumber = (docNumber) => {
  if (!docNumber) return '';
  const docStr = String(docNumber).trim();
  // Si tiene guiones (formato 20-30123123-7), extraer el número del medio
  if (docStr.includes('-')) {
    const parts = docStr.split('-');
    if (parts.length === 3) {
      return parts[1]; // Retornar solo el DNI del medio
    }
  }
  // Si tiene 11 dígitos sin guiones (formato 20301231237), extraer del medio
  if (/^\d{11}$/.test(docStr)) {
    const cleanedDni = docStr.substring(2, 10); // Extraer los 8 dígitos del medio
    return cleanedDni;
  }
  // Si ya está limpio, devolverlo tal cual
  return docStr;
};
/**
 * Normalizar nombre de provincia para que coincida con el select
 * BUENOS AIRES → Buenos Aires
 */
export const normalizeProvincia = (provincia) => {
  if (!provincia) return '';
  const provinciaStr = String(provincia).trim();
  // Lista de provincias válidas en el formato correcto
  const provinciasValidas = [
    'Buenos Aires', 'CABA', 'Catamarca', 'Chaco', 'Chubut',
    'Córdoba', 'Corrientes', 'Entre Ríos', 'Formosa', 'Jujuy',
    'La Pampa', 'La Rioja', 'Mendoza', 'Misiones', 'Neuquén',
    'Río Negro', 'Salta', 'San Juan', 'San Luis', 'Santa Cruz',
    'Santa Fe', 'Santiago del Estero', 'Tierra del Fuego', 'Tucumán'
  ];
  // Buscar coincidencia ignorando mayúsculas/minúsculas
  const provinciaEncontrada = provinciasValidas.find(
    p => p.toLowerCase() === provinciaStr.toLowerCase()
  );
  if (provinciaEncontrada) {
    return provinciaEncontrada;
  }
  return provinciaStr;
};
/**
 * Hook para mapear datos de la API al formato del formulario
 */
export const useScoringDataMapper = () => {
  /**
   * Mapear datos del titular desde la API al formulario
   */
  const mapTitularData = useCallback((titularData, planData = null) => {
    if (!titularData) {
      return {};
    }
    // Logs de extracción de IDs para campos de selección
    const mapped = {
      // Datos personales - Usar nombres exactos de la API según Swagger
      // tipoPersona almacena el valor completo (ej: "2 - Persona Jurídica") para compatibilidad con el backend
      tipoPersona: titularData.personType || titularData.tipoPersona || titularData.type || '',
      titular: titularData.completeName || titularData.personCompleteName || titularData.mainName || titularData.titular || titularData.name || '',
      cuit: cleanDocumentNumber(titularData.documentNumber || titularData.personDocumentNumber || titularData.cuil || titularData.cuit || titularData.dni || ''),
      // Campos específicos de Persona Jurídica (vacíos para Persona Física)
      nombreEmpresa: titularData.completeName || '',
      cuitEmpresa: titularData.documentNumber || titularData.personDocumentNumber || '',
      fechaConstitucion: titularData.companyDate || '',
      tipoEmpresa: titularData.companyType || '',
      objetoEmpresa: titularData.companyPurpose || '',
      ingresosEmpresa: titularData.income || '',
      pepEmpresa: titularData.pep || '',
      telefonoEmpresa: titularData.phone || '',
      emailEmpresa: titularData.mail || '',
      fechaNacimiento: (() => {
        const fechaOriginal = titularData.personBirthDate || titularData.birthDate || titularData.fechaNacimiento || '';
        const fechaConvertida = convertirFechaParaInput(fechaOriginal);
        return fechaConvertida;
      })(),
      estadoCivil: extractIdFromText(titularData.personMarriageStatus || titularData.marriageStatus || titularData.estadoCivil || ''),
      sexo: extractIdFromText(titularData.personGender || titularData.gender || titularData.sexo || ''),
      nacionalidad: titularData.personNationality || titularData.nationality || titularData.nacionalidad || '',
      // Datos laborales y fiscales
      antiguedadLaboral: titularData.personlaborTime || titularData.laborTime || titularData.antiguedadLaboral || '',
      tipoIngreso: extractIdFromText(titularData.incomeType || titularData.tipoIngreso || ''),
      ingresosNetos: titularData.income || titularData.ingresosNetos || '',
      categoriaFiscal: extractIdFromText(titularData.taxCategory || titularData.categoriaFiscal || ''),
      pep: titularData.pep || '',
      // Dirección
      direccion: titularData.address || titularData.direccion || '',
      numero: titularData.addressNumber || titularData.numero || '',
      pisoDpto: titularData.addressFloor || titularData.pisoDpto || '',
      codigoPostal: titularData.addressPostalCode || titularData.codigoPostal || '',
      localidad: titularData.addressLocale || titularData.localidad || '',
      provincia: normalizeProvincia(titularData.addressProvince || titularData.provincia || ''),
      // Contacto
      telefono: titularData.phone || titularData.telefono || '',
      email: titularData.mail || titularData.email || '',
    };
    // Si hay datos del plan, agregarlos (puede ser el mismo objeto)
    if (planData) {
      // Intentar múltiples posibles nombres de campos
      mapped.modelo = planData.productName || planData.modelo || planData.product || '';
      mapped.valorMovil = planData.productValue 
        ? (typeof planData.productValue === 'number' ? planData.productValue.toLocaleString('es-AR', { minimumFractionDigits: 2 }) : planData.productValue)
        : planData.valorMovil || '';
      mapped.mesaPlan = planData.monthsPlan?.toString() 
        || planData.mesaPlan?.toString() 
        || planData.plan?.toString() 
        || '';
      mapped.saldoPagar = planData.debtValue 
        ? (typeof planData.debtValue === 'number' ? planData.debtValue.toLocaleString('es-AR', { minimumFractionDigits: 2 }) : planData.debtValue)
        : planData.saldoPagar || '';
      mapped.porcentajeDeuda = planData.debtPercent 
        ? `${planData.debtPercent}%`
        : planData.porcentajeDeuda || '0%';
      mapped.valorCuota = planData.futureValue 
        ? (typeof planData.futureValue === 'number' ? planData.futureValue.toLocaleString('es-AR', { minimumFractionDigits: 2 }) : planData.futureValue)
        : planData.valorCuota || '0';
      mapped.cuotasVencer = planData.quotasLeft?.toString() 
        || planData.cuotasVencer?.toString() 
        || '';
      mapped.status = planData.status || planData.estado || '';
      mapped.tipoAdjudicacion = planData.adjType 
        || planData.tipoAdjudicacion 
        || planData.awardType 
        || '';
      mapped.fechaAdjudicacion = (() => {
        const fechaOriginal = planData.adjDate || planData.fechaAdjudicacion || planData.awardDate || '';
        const fechaConvertida = convertirFechaParaInput(fechaOriginal);
        return fechaConvertida;
      })();
      mapped.fechaAceptacion = (() => {
        const fechaOriginal = planData.acceptDate || planData.fechaAceptacion || '';
        const fechaConvertida = convertirFechaParaInput(fechaOriginal);
        return fechaConvertida;
      })();
      // Mapear concesionario - FORZADO a 01050_MAIPU por el momento
      mapped.concesionario = '01050_MAIPU';
    }
    const camposVacios = Object.entries(mapped).filter(([k, v]) => !v || v === '').map(([k]) => k);
    const camposLlenos = Object.entries(mapped).filter(([k, v]) => v && v !== '').length;
    if (camposVacios.length > 0) {
    }
    return mapped;
  }, []);
  /**
   * Mapear datos del formulario para enviar a la API
   */
  const mapFormToAPI = useCallback((formData, garantesData = []) => {
    return {
      group: formData.grupo,
      order: formData.orden,
      titularData: {
        personType: formData.tipoPersona,
        personBirthDate: formData.fechaNacimiento,
        personMarriageStatus: formData.estadoCivil,
        personGender: formData.sexo,
        personNationality: formData.nacionalidad,
        personlaborTime: formData.antiguedadLaboral,
        documentNumber: formData.cuit,
        completeName: formData.titular,
        taxCategory: formData.categoriaFiscal,
        address: formData.direccion,
        addressNumber: formData.numero,
        addressFloor: formData.pisoDpto,
        addressPostalCode: formData.codigoPostal,
        addressLocale: formData.localidad,
        addressProvince: formData.provincia,
        incomeType: formData.tipoIngreso,
        income: formData.ingresosNetos,
        pep: formData.pep,
        phone: formData.telefono,
        mail: formData.email,
      },
      apodData: garantesData.map(garante => ({
        nombreApellido: garante.nombreApellido,
        dni: garante.dni,
        fechaNacimiento: garante.fechaNacimiento,
        estadoCivil: garante.estadoCivil,
        sexo: garante.sexo,
        calle: garante.calle,
        numero: garante.numero,
        altura: garante.altura,
        codigoPostal: garante.codigoPostal,
        provincia: garante.provincia,
        telefono: garante.telefono,
        email: garante.email,
        tipoIngreso: garante.tipoIngreso,
        ingresosNetos: garante.ingresosNetos,
        antiguedadLaboral: garante.antiguedadLaboral,
        nacionalidad: garante.nacionalidad,
        condicionFiscal: garante.condicionFiscal,
      })),
    };
  }, []);
  return {
    mapTitularData,
    mapFormToAPI,
  };
};
